import { createContext, useContext, useMemo, useState } from "react";
import type { ChatJobContext } from "@/lib/findigoChat/types";
import { logWarning } from "@/lib/logger";

interface FindigoChatContextValue {
  currentJob: ChatJobContext | null;
  setCurrentJob: (job: ChatJobContext) => void;
  clearCurrentJob: () => void;
}

const FindigoChatContext = createContext<FindigoChatContextValue | undefined>(undefined);
const FALLBACK_CONTEXT: FindigoChatContextValue = {
  currentJob: null,
  setCurrentJob: () => {},
  clearCurrentJob: () => {},
};

export const FindigoChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentJob, setCurrentJobState] = useState<ChatJobContext | null>(null);

  const value = useMemo(
    () => ({
      currentJob,
      setCurrentJob: (job: ChatJobContext) => setCurrentJobState(job),
      clearCurrentJob: () => setCurrentJobState(null),
    }),
    [currentJob],
  );

  return <FindigoChatContext.Provider value={value}>{children}</FindigoChatContext.Provider>;
};

export const useFindigoChatContext = (): FindigoChatContextValue => {
  const context = useContext(FindigoChatContext);
  if (!context) {
    logWarning("useFindigoChatContext used without provider");
    return FALLBACK_CONTEXT;
  }
  return context;
};
